// Clinical record attachments: what may be attached, where it is stored, and
// when a removed one is deleted (TGCLOUD-1850).
//
// The server holds the authoritative rules (backend/util/clinicalAttachments.js)
// and checks the bytes as well; this copy exists so a file that would be
// refused is refused BEFORE a 50 MB upload, with the reason on its row.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';

export const MAX_ATTACHMENT_MB = 50;
export const MAX_ATTACHMENT_BYTES = MAX_ATTACHMENT_MB * 1024 * 1024;

// By extension, as the server allows. SVG is deliberately absent (it can carry
// script); `image/*` used to admit it.
export const ATTACHMENT_EXTENSIONS = [
  'pdf',
  'txt',
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'bmp',
  'tif',
  'tiff',
  'heic',
  'heif'
];

// For the file picker, so it offers what will be accepted.
export const ACCEPT_ATTR = ATTACHMENT_EXTENSIONS.map((e) => `.${e}`).join(',');

export const ACCEPTED_SUMMARY = `PDF, images or text files · up to ${MAX_ATTACHMENT_MB} MB each`;

const extensionOf = (name) => {
  const s = String(name || '');
  const dot = s.lastIndexOf('.');
  return dot > 0 ? s.slice(dot + 1).toLowerCase() : '';
};

// Why a file cannot be attached, or null if it can.
export function validateAttachment(file) {
  if (!ATTACHMENT_EXTENSIONS.includes(extensionOf(file?.name))) {
    return 'Not an accepted type — attach a PDF, an image or a .txt file.';
  }
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return `Larger than the ${MAX_ATTACHMENT_MB} MB limit.`;
  }
  if (file.size === 0) return 'This file is empty.';
  return null;
}

// The stored object key: `<first 9 characters of familyMemberId>/<name>` — the
// family's G-number folder. The upload route derives it the same way. Null
// without a familyMemberId, since no key can be formed.
export const storageKey = (familyId, name) =>
  familyId ? `${String(familyId).slice(0, 9)}/${name}` : null;

// A failed upload, as a sentence for the file's row, and whether trying again
// could help (a refusal of the file itself will not).
export function describeUploadError(err) {
  const status = err?.response?.status;
  const serverMessage = err?.response?.data?.message;
  if (status === 413) {
    return {
      message: `Larger than the ${MAX_ATTACHMENT_MB} MB limit.`,
      retriable: false
    };
  }
  if (status === 415 || status === 400) {
    return {
      message: serverMessage || 'This file cannot be attached.',
      retriable: false
    };
  }
  if (status === 403 && err?.response?.data?.groupAccess) {
    return {
      message: 'This workspace is read-only, so files cannot be added.',
      retriable: false
    };
  }
  // A refusal of the caller, not of the file: their role, or a person in a
  // family they may not see. Trying again would be refused again.
  if (status === 403) {
    return {
      message:
        serverMessage || 'You do not have access to add files to this record.',
      retriable: false
    };
  }
  if (!status) {
    return {
      message: 'Upload failed — check your connection and try again.',
      retriable: true
    };
  }
  return {
    message: serverMessage || `Upload failed (error ${status}).`,
    retriable: true
  };
}

// Delete, from storage, the attachments a just-saved record no longer lists.
//
// THE RULE. After a successful save, delete every name that was on the record
// when the form opened (`before`) or was uploaded while it was open
// (`uploaded`), and is not on the record as saved (`after`). Nothing is
// deleted until the save succeeds, so Remove followed by Cancel leaves the
// saved record — and its files — as they were. The server additionally keeps
// any object another record still names (same family folder, same name).
//
// Best effort: the record is already saved, so a failed delete leaves an
// orphaned object rather than failing anything.
export async function deleteRemovedAttachments({
  before = [],
  after = [],
  uploaded = [],
  familyId
}) {
  if (!familyId) return [];
  const keep = new Set(after || []);
  const removed = [...new Set([...(before || []), ...(uploaded || [])])].filter(
    (name) => typeof name === 'string' && name && !keep.has(name)
  );
  await Promise.allSettled(
    removed.map((name) =>
      axios
        .delete(APICONSTANTS.fileAttachment, {
          params: { fileName: storageKey(familyId, name) }
        })
        .catch((err) => {
          console.warn(`Attachment ${name} was not deleted:`, err?.message);
          throw err;
        })
    )
  );
  return removed;
}

// A form's record of its attachment changes, for deleteRemovedAttachments.
//   trackUpload(name)       call for every file uploaded while the form is open
//   setBaseline(names)      the list as saved, when it arrives after mount
//   commit(after, familyId) call once the save has succeeded
export function useAttachmentChanges(savedNames) {
  const baseline = useRef(null);
  if (baseline.current === null) baseline.current = [...(savedNames || [])];
  const uploaded = useRef(new Set());

  const trackUpload = useCallback((name) => {
    if (name) uploaded.current.add(name);
  }, []);
  const setBaseline = useCallback((names) => {
    baseline.current = [...(names || [])];
  }, []);
  const commit = useCallback((after, familyId) => {
    const promise = deleteRemovedAttachments({
      before: baseline.current,
      after,
      uploaded: [...uploaded.current],
      familyId
    });
    baseline.current = [...(after || [])];
    uploaded.current = new Set();
    return promise;
  }, []);

  return useMemo(
    () => ({ trackUpload, setBaseline, commit }),
    [trackUpload, setBaseline, commit]
  );
}

// A signed URL is a bearer link that lapses — an hour on cloud storage, five
// minutes on local disk — while a record dialog or card can stay open far
// longer. A link held past that still looks clickable and opens the storage
// provider's "request has expired" page. So each one is asked for again a
// little before it lapses: the server says how long it lives (`expiresIn`,
// seconds, from GET /clinical-notes/file), and a link that does not say — an
// upload's, or an older server's — is taken to live the shortest time.
export const DEFAULT_LINK_SECONDS = 300;
const RENEW_MARGIN_MS = 30 * 1000;

export const renewAtFor = (expiresIn, now = Date.now()) => {
  const seconds =
    Number(expiresIn) > 0 ? Number(expiresIn) : DEFAULT_LINK_SECONDS;
  const lifetime = seconds * 1000;
  return now + Math.max(lifetime - RENEW_MARGIN_MS, lifetime / 2);
};

// Signed URLs for a list of attachment names, keyed by NAME (so removing one
// cannot shift the others onto the wrong file), resolved for any name that has
// none yet and renewed before each lapses. `known` supplies URLs already in
// hand — an upload returns one.
export function useAttachmentLinks(names, familyId, known) {
  // name → { url, renewAt }
  const [links, setLinks] = useState({});
  const [resolving, setResolving] = useState(null);
  // Bumped when a link falls due, or the page is looked at again, so the
  // resolver runs for whatever is due by then.
  const [round, setRound] = useState(0);
  // When each `known` URL was first seen, for its renewal time: url → renewAt.
  const knownSeen = useRef(new Map());
  const list = (names || []).filter((n) => typeof n === 'string' && n);
  const listKey = `${familyId || ''}#${list.join('|')}`;

  // The later-renewing of what was resolved and what an upload supplied.
  const entryFor = (name) => {
    const resolved = links[name];
    const url = known?.[name];
    if (!url) return resolved || null;
    if (!knownSeen.current.has(url)) knownSeen.current.set(url, renewAtFor());
    const supplied = { url, renewAt: knownSeen.current.get(url) };
    return resolved && resolved.renewAt >= supplied.renewAt
      ? resolved
      : supplied;
  };

  useEffect(() => {
    if (!familyId) return undefined;
    const now = Date.now();
    const due = list.filter((name) => !(entryFor(name)?.renewAt > now));
    if (!due.length) return undefined;
    let cancelled = false;
    setResolving(listKey);
    (async () => {
      const resolved = {};
      for (const name of due) {
        try {
          const response = await axios.get(APICONSTANTS.fileAttachment, {
            params: { fileName: storageKey(familyId, name) }
          });
          const url = response?.data?.fileURL;
          if (url)
            resolved[name] = {
              url,
              renewAt: renewAtFor(response.data.expiresIn)
            };
        } catch (err) {
          // Left unresolved: the row then says it is unavailable rather than
          // looking clickable and doing nothing.
          void err;
        }
      }
      if (cancelled) return;
      setLinks((prev) => {
        const next = { ...prev, ...resolved };
        // One that could not be renewed is dropped rather than left to lapse
        // while still looking like a link.
        for (const name of due) if (!resolved[name]) delete next[name];
        return next;
      });
      setResolving(null);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listKey, round]);

  // Wake the resolver when the next link falls due.
  useEffect(() => {
    const now = Date.now();
    let next = Infinity;
    for (const name of list) {
      const at = entryFor(name)?.renewAt;
      if (at > now && at < next) next = at;
    }
    if (next === Infinity) return undefined;
    const id = setTimeout(() => setRound((r) => r + 1), next - now);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [links, known, listKey]);

  // A hidden tab's timers are throttled and a sleeping machine's do not run,
  // so check again whenever the page is looked at.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') setRound((r) => r + 1);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const merged = useMemo(() => {
    const out = {};
    for (const name of list) {
      const entry = entryFor(name);
      if (entry) out[name] = entry.url;
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [links, known, listKey]);
  return { links: merged, pending: resolving === listKey };
}
